"use client";

import Image from "next/image";
import Link from "next/link";
import { Menu, Phone, X } from "lucide-react";
import { useEffect, useState } from "react";

import { mainNav } from "@/config/navigation";
import { siteConfig } from "@/config/site";
import { cn } from "@/lib/utils";

export function SiteHeader() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <header className="fixed inset-x-0 top-3 z-50 px-3 sm:top-5 sm:px-6">
      <div className="mx-auto w-full max-w-6xl">
        <nav
          aria-label="Hlavní navigace"
          className="glass-pill flex items-center justify-between gap-3 rounded-full px-3 py-2 pl-4"
        >
          <Link
            href="#uvod"
            className="flex shrink-0 items-center gap-3 rounded-full"
            aria-label={`${siteConfig.name} — úvod`}
          >
            <span className="flex h-9 items-center rounded-md bg-white px-2 shadow-[0_1px_2px_rgba(0,0,0,0.35)]">
              <Image
                src="/uploads/logo.png"
                alt=""
                width={1170}
                height={750}
                priority
                className="h-6 w-auto"
              />
            </span>
            <span className="hidden text-sm font-semibold tracking-tight text-white sm:block">
              {siteConfig.name}
            </span>
          </Link>

          <ul className="hidden items-center gap-1 lg:flex">
            {mainNav.map((item) => (
              <li key={item.href}>
                <Link
                  href={item.href}
                  className="rounded-full px-3 py-2 text-sm font-medium text-[#9fb0c1] transition-colors hover:bg-white/10 hover:text-white"
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>

          <div className="flex items-center gap-2">
            <a
              href={siteConfig.contact.phoneHref}
              className="hidden items-center gap-2 rounded-full px-3 py-2 text-sm font-medium text-[#9fb0c1] transition-colors hover:text-white xl:flex"
            >
              <Phone className="size-3.5" aria-hidden="true" />
              {siteConfig.contact.phone}
            </a>
            <Link
              href="#kontakt"
              className="hidden rounded-full bg-white px-4 py-2 text-sm font-semibold text-[#16283c] shadow-[inset_0_1px_0_rgba(255,255,255,0.9),0_6px_18px_-8px_rgba(0,0,0,0.7)] transition-transform hover:-translate-y-0.5 sm:inline-flex"
            >
              Kontaktovat
            </Link>
            <button
              type="button"
              onClick={() => setOpen((value) => !value)}
              aria-expanded={open}
              aria-controls="mobile-nav"
              aria-label={open ? "Zavřít menu" : "Otevřít menu"}
              className="inline-flex size-10 items-center justify-center rounded-full text-white ring-1 ring-white/15 transition-colors hover:bg-white/10 lg:hidden"
            >
              {open ? <X className="size-5" /> : <Menu className="size-5" />}
            </button>
          </div>
        </nav>

        <div
          id="mobile-nav"
          className={cn(
            "glass-pill mt-2 overflow-hidden rounded-3xl transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] lg:hidden",
            open ? "max-h-[26rem] opacity-100" : "pointer-events-none max-h-0 opacity-0",
          )}
        >
          <ul className="flex flex-col p-3">
            {mainNav.map((item) => (
              <li key={item.href}>
                <Link
                  href={item.href}
                  onClick={() => setOpen(false)}
                  className="block rounded-2xl px-4 py-3 text-sm font-medium text-white/80 transition-colors hover:bg-white/10 hover:text-white"
                >
                  {item.label}
                </Link>
              </li>
            ))}
            <li className="mt-1">
              <Link
                href="#kontakt"
                onClick={() => setOpen(false)}
                className="block rounded-2xl bg-white px-4 py-3 text-center text-sm font-semibold text-[#16283c]"
              >
                Kontaktovat
              </Link>
            </li>
          </ul>
        </div>
      </div>
    </header>
  );
}
